import { useInfiniteQuery } from "@tanstack/react-query";
import { PEXELS_PHOTOS } from "../../constants/QueryKeys";
import PexelsService from "../../service/PexelsService";

/**
 * @typedef {import("@/types/data/Pexels.mjs").PexelsPhoto} PexelsPhoto
 * @typedef {import("@/types/data/Pexels.mjs").PexelsPage} PexelsPage
 * @typedef {import("@/types/data/Pexels.mjs").PexelsOrientation} PexelsOrientation
 * @typedef {import("@/types/data/Pexels.mjs").PexelsError} PexelsError
 */

/**
 * @typedef {import("@tanstack/react-query").UseInfiniteQueryResult<
 *   import("@tanstack/react-query").InfiniteData<PexelsPage, number>,
 *   PexelsError
 * >} PexelsPhotosQueryResult
 */

/**
 * @typedef {Partial<import("@tanstack/react-query").UseInfiniteQueryOptions<
 *   PexelsPage, PexelsError, import("@tanstack/react-query").InfiniteData<PexelsPage, number>,
 *   Array<string>, number
 * >>} PexelsPhotosQueryOptions
 */

const defaults = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  // Mount-refetching an infinite query reloads every accumulated page, against
  // a rate-limited API the endpoint already caches for.
  refetchOnMount: false,
  staleTime: 5 * 60 * 1000,
  // Auth and rate-limit failures will not succeed on retry.
  retry: false,
};

/**
 * @param {{query?: string, orientation?: PexelsOrientation}} params
 * @param {PexelsPhotosQueryOptions} [options]
 * @returns {{ photos: PexelsPhoto[], totalResults: number } & PexelsPhotosQueryResult}
 */
const usePexelsPhotos = ({ query = "", orientation = "" }, options = {}) => {
  const result = useInfiniteQuery({
    queryKey: [PEXELS_PHOTOS, query.trim(), orientation],
    /** @param {{pageParam: number}} context */
    queryFn: ({ pageParam }) => PexelsService.fetchPhotos({ query, orientation, page: pageParam }),
    initialPageParam: 1,
    /** @param {PexelsPage} lastPage */
    getNextPageParam: (lastPage) => lastPage.nextPage,
    ...defaults,
    ...options,
  });

  const pages = result.data?.pages ?? [];
  const photos = pages.flatMap((page) => page.photos);
  const totalResults = pages[0]?.totalResults ?? 0;

  return { photos, totalResults, ...result };
};

export default usePexelsPhotos;
